import type * as React from 'react'
import { useEffect } from 'react'
import { useDispatch } from 'react-redux'
import type { Dispatch } from 'redux'

import {
  Dialog,
  Button,
  Flex,
  Heading,
  Text,
  CheckIcon,
  XIcon,
  IconButton,
} from '@buffer-mono/popcorn'
import {
  BufferTrackerReact as BufferTracker,
  Client,
} from '@buffer-mono/tracking-plan'

import { MODALS, openGlobalModal } from '~publish/hooks/useOpenGlobalModal'
import { SEGMENT_NAMES } from '~publish/legacy/constants'
import { actions as modalActions } from '~publish/legacy/modals/reducer'

import {
  useAccount,
  useCanStartTrial,
} from '../../../../contexts/AccountContext'

import styles from './TrialCollaborationModal.module.css'

const onUpsellClick = ({
  dispatch,
  canStartTrial,
}: {
  dispatch: Dispatch
  canStartTrial: boolean
}): void => {
  const upgradePath = canStartTrial ? 'trial' : 'upgrade'
  openGlobalModal(MODALS.planSelector, {
    cta: SEGMENT_NAMES.COMPOSER_REQUEST_APPROVAL_UPSELL,
    upgradePathName: `approvals-${upgradePath}`,
  })

  dispatch(modalActions.hideTrialCollaborationModal())
}

const TrialCollaborationModal: React.FC = () => {
  const dispatch = useDispatch()
  const user = useAccount()
  const canStartTrial = useCanStartTrial()
  const headingText = canStartTrial
    ? 'Try our collaboration features for free!'
    : 'Try collaboration features on the Team plan'

  const commonTrackingProps =
    user?.account?.currentOrganization?.commonTrackingProperties || null

  useEffect(() => {
    BufferTracker.pageViewed({
      name: 'Draft Paywall Modal',
      product: 'publish',
      cta: 'Drafts Request Approval',
      path: window.location.pathname || null,
    })

    const upgradePath = canStartTrial ? 'trial' : 'upgrade'

    BufferTracker.upgradePathViewed({
      organizationId: user?.account?.currentOrganization?.id || '',
      upgradePathName: `approvals-${upgradePath}`,
      product: 'publish',
      clientName: Client.PublishWeb,
      cta: SEGMENT_NAMES.COMPOSER_REQUEST_APPROVAL_UPSELL,
    })
  }, [user, commonTrackingProps, canStartTrial])

  const handleStartStrialClick = (): void => {
    dispatch(modalActions.hideTrialCollaborationModal())
    openGlobalModal(MODALS.startTrial, {
      ctaView: 'trialCollaborationModal',
      ctaLocation: 'draft',
      cta: 'startTrial',
      ctaButton: 'startTrial',
      upgradePathName: 'approvals-trial',
    })
  }

  return (
    <Dialog
      open={true}
      onOpenChange={() => dispatch(modalActions.hideTrialCollaborationModal())}
    >
      <Dialog.Content size="large" className={styles.dialogContent}>
        <Dialog.Body className={styles.dialogBody}>
          <Flex
            direction="row"
            justify="end"
            className={styles.closeButtonContainer}
          >
            <IconButton
              variant="tertiary"
              label="Close modal"
              onClick={() =>
                dispatch(modalActions.hideTrialCollaborationModal())
              }
              className={styles.closeButton}
            >
              <XIcon />
            </IconButton>
          </Flex>

          <Flex direction="column" gap="lg" className={styles.contentContainer}>
            <Heading size="large" className={styles.heading}>
              {headingText}
            </Heading>
            <Text color="subtle">
              Send drafts for approval or review other&apos;s drafts plus...
            </Text>

            <Flex direction="column" gap="xs">
              <Text color="subtle" icon={<CheckIcon color="brand" />}>
                Unlocked publishing, analytics and engagement
              </Text>
              <Text color="subtle" icon={<CheckIcon color="brand" />}>
                Unlimited seats for your team
              </Text>
              <Text color="subtle" icon={<CheckIcon color="brand" />}>
                Easy, clear exportable reports
              </Text>
            </Flex>
          </Flex>

          {canStartTrial && (
            <Flex
              direction="row"
              align="center"
              gap="md"
              className={styles.buttonContainer}
            >
              <Button
                id="collab-start-trial-cta"
                variant="primary"
                onClick={handleStartStrialClick}
              >
                Start a 14-day Free Trial
              </Button>
              <Button
                variant="secondary"
                onClick={() => onUpsellClick({ dispatch, canStartTrial })}
              >
                See Paid Plans
              </Button>
            </Flex>
          )}
          {!canStartTrial && (
            <Button
              variant="primary"
              onClick={() => onUpsellClick({ dispatch, canStartTrial })}
            >
              Discover the Team plan
            </Button>
          )}
        </Dialog.Body>
      </Dialog.Content>
    </Dialog>
  )
}

export default TrialCollaborationModal
